import { NETWORK_BASE_URL, NETWORK_KEY } from '../constants'

export type FromBackendNetworkStop = {
  accessibilityCodes?: ('MA' | 'MB' | 'VB')[]
  classification?: 'REGULAR' | 'TEMPORARY' | 'COMBI'
  coordinate: { latitude: number; longitude: number }
  distanceFromOrigin?: number
  id?: string
  lineDirectionIds?: string[]
  location?: string
  longDescription: string
  municipality: { id: string; description: string; nisCode: string }
  newStopId?: string
  oldStopId?: string
  public: boolean
  shortDescription: string
  status: 'ACTIVE' | 'CLOSED' | 'PLANNED'
  type: 'UNCHANGED' | 'TEMPORARY'
  validFrom: string
  validTo: string
}

export type FromBackendNetworkLineDirection = {
  description: string // "Silsburg - Berchem Station - P+R Linkeroever"
  directionCode: string
  id: string // "10091"
  lastInPlanning: string // "2024-06-30"
  lineCategory: string // "Kernnet"
  lineColor: {
    background: string // "#EF7D00"
    backgroundBorder: string // "#EF7D00"
    backgroundBorderCode: string // "OR"
    backgroundBorderDescription: string // "Oranje"
    backgroundCode: string // "OR"
    backgroundDescription: string // "Oranje"
    foreground: string // "#000000"
    foregroundBorder: string // "#000000"
    foregroundBorderCode: string // "ZW"
    foregroundBorderDescription: string // "DeLijn zwart"
    foregroundCode: string // "ZW"
    foregroundDescription: string // "DeLijn zwart"
  }
  lineId: string // "1009"
  nextInPlanning: string[] // ['2024-05-08', '2024-05-09']
  publicLineNr: string // "9"
  servedStopIds: string[] // ['101049', '101290']
  serviceType: string // "0"
  status: string // "ACTIVE"
  transportAuthority: string // "AN"
  transportType: string // "1"
  validFrom: string // "2024-04-01"
  validTo: string // "2024-08-31"
}

export function mapStopIdToDummyStop(stopId: string) {
  return {
    id: stopId,
    shortDescription: stopId,
    longDescription: stopId,
  }
}

export async function getMultipleLineDirections(
  lineDirectionIds: string[],
  date?: string,
): Promise<FromBackendNetworkLineDirection[]> {
  if (lineDirectionIds.length === 0) {
    return []
  }
  // prettier-ignore
  let url = `${NETWORK_BASE_URL}/linedirections?ids=${lineDirectionIds.join(',')}`
  if (date) {
    url += `&date=${date}`
  }
  const result = await fetch(url, {
    headers: {
      'Ocp-Apim-Subscription-Key': NETWORK_KEY as string,
    },
  }).then((data) => data.json())
  return result.data
}

/**
 * Retrieves information for multiple stops.
 * If an error occurs during the fetch, it returns an array of stop information objects.
 * Each object contains a short description and a long description, both using the stop ID.
 */
export async function getMultipleStops(
  stopIds: string[],
): Promise<FromBackendNetworkStop[]> {
  if (!stopIds.length) {
    return []
  }
  const url = `${NETWORK_BASE_URL}/stops?ids=${stopIds.join(',')}`
  const data = await fetch(url, {
    headers: {
      'Ocp-Apim-Subscription-Key': NETWORK_KEY as string,
    },
  })

  const { data: stops }: { data: FromBackendNetworkStop[] } = await data.json()

  if (stops.length !== stopIds.length) {
    return stopIds.map((stopId) => {
      const foundStop = stops.find((stop) => stop.id === stopId)
      return foundStop || mapStopIdToDummyStop(stopId)
    }) as FromBackendNetworkStop[]
  }
  return stops
}
